import { useState, useEffect, useCallback } from "react";
import { API, authFetch, useAuth } from "@/App";
import { toast } from "sonner";
import { CheckSquare, Plus, User, Clock, AlertTriangle, Camera, Image, X, MessageSquare } from "lucide-react";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";

const STATUS_COLORS = {
  OPEN: "bg-blue-600/20 text-blue-400 border-blue-600/30",
  IN_PROGRESS: "bg-amber-600/20 text-amber-400 border-amber-600/30",
  DONE: "bg-emerald-600/20 text-emerald-400 border-emerald-600/30",
};

const PRIORITY_COLORS = {
  LOW: "bg-slate-600/20 text-slate-400", MEDIUM: "bg-blue-600/20 text-blue-400",
  HIGH: "bg-amber-600/20 text-amber-400", URGENT: "bg-red-600/20 text-red-400",
};

const FactoryTasksPage = () => {
  const { user } = useAuth();
  const [tasks, setTasks] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showAdd, setShowAdd] = useState(false);
  const [creating, setCreating] = useState(false);
  const [statusFilter, setStatusFilter] = useState("all");
  const [users, setUsers] = useState([]);
  const [selectedTask, setSelectedTask] = useState(null);
  const [comment, setComment] = useState("");
  const [form, setForm] = useState({ title: "", description: "", assigned_to: "", assigned_to_name: "", priority: "MEDIUM", due_date: "", unit: user?.assigned_unit || "" });
  const [photoFiles, setPhotoFiles] = useState([]);

  const fetchTasks = useCallback(async () => {
    try {
      const params = statusFilter !== "all" ? `?status=${statusFilter}` : "";
      const res = await authFetch(`${API}/factory/tasks${params}`);
      const data = await res.json();
      if (data.success) setTasks(data.data || []);
    } catch { toast.error("Failed to load tasks"); }
    finally { setLoading(false); }
  }, [statusFilter]);

  const fetchUsers = async () => {
    try {
      const res = await authFetch(`${API}/admin/users`);
      const data = await res.json();
      if (data.success) setUsers(data.data.filter(u => u.role === "FACTORY_ADMIN" || u.role === "SUPER_ADMIN"));
    } catch { /* ignore */ }
  };

  useEffect(() => { fetchTasks(); fetchUsers(); }, [fetchTasks]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setCreating(true);
    try {
      let photos = [];
      if (photoFiles.length > 0) {
        const fd = new FormData();
        photoFiles.forEach(f => fd.append("files", f));
        const upRes = await authFetch(`${API}/upload-multiple?category=factory-tasks`, { method: "POST", body: fd });
        const upData = await upRes.json();
        if (upData.success) photos = upData.data.map(d => d.storage_path);
      }
      const res = await authFetch(`${API}/factory/tasks`, {
        method: "POST", headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ ...form, photos }),
      });
      if ((await res.json()).success) {
        toast.success("Task created");
        setShowAdd(false);
        setForm({ title: "", description: "", assigned_to: "", assigned_to_name: "", priority: "MEDIUM", due_date: "", unit: user?.assigned_unit || "" });
        setPhotoFiles([]);
        fetchTasks();
      }
    } catch { toast.error("Failed"); }
    finally { setCreating(false); }
  };

  const updateStatus = async (taskId, newStatus) => {
    await authFetch(`${API}/factory/tasks/${taskId}`, {
      method: "PUT", headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ status: newStatus }),
    });
    toast.success(`Status: ${newStatus}`);
    fetchTasks();
  };

  const addComment = async () => {
    if (!comment.trim() || !selectedTask) return;
    await authFetch(`${API}/factory/tasks/${selectedTask.task_id}/comment`, {
      method: "POST", headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ text: comment }),
    });
    setComment("");
    const res = await authFetch(`${API}/factory/tasks?status=all`);
    const data = await res.json();
    if (data.success) {
      const updated = data.data.find(t => t.task_id === selectedTask.task_id);
      if (updated) setSelectedTask(updated);
      setTasks(data.data);
    }
  };

  const counts = { OPEN: 0, IN_PROGRESS: 0, DONE: 0 };
  tasks.forEach(t => { if (counts[t.status] !== undefined) counts[t.status]++; });

  return (
    <div className="space-y-6" data-testid="factory-tasks-page">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <h1 className="text-2xl font-bold text-white font-['Sora']">Tasks</h1>
          <p className="text-emerald-600 text-sm mt-1">Assign and track tasks between admin & supervisors</p>
        </div>
        <Button onClick={() => setShowAdd(true)} className="bg-emerald-600 hover:bg-emerald-700 text-white" data-testid="add-task-btn">
          <Plus className="w-4 h-4 mr-2" /> New Task
        </Button>
      </div>

      <div className="flex gap-2 flex-wrap">
        {["all", "OPEN", "IN_PROGRESS", "DONE"].map(s => (
          <button key={s} onClick={() => setStatusFilter(s)}
            className={`px-4 py-1.5 rounded-lg text-xs font-medium transition-colors ${statusFilter === s ? "bg-emerald-600 text-white" : "bg-[#1C2B2B] text-slate-400 border border-emerald-900/30 hover:text-white"}`}
            data-testid={`filter-${s.toLowerCase()}`}>
            {s === "all" ? "All" : s.replace("_", " ")} {s !== "all" ? `(${counts[s] || 0})` : ""}
          </button>
        ))}
      </div>

      <div className="space-y-3">
        {loading ? <div className="text-center py-12"><div className="w-8 h-8 spinner mx-auto" /></div> :
          tasks.length === 0 ? <Card className="bg-[#1C2B2B] border-emerald-900/30 p-8 text-center"><p className="text-slate-400">No tasks found</p></Card> :
          tasks.map(t => (
            <Card key={t.task_id} className="bg-[#1C2B2B] border-emerald-900/30 p-4 hover:border-emerald-700/50 transition-colors cursor-pointer" onClick={() => setSelectedTask(t)} data-testid={`task-${t.task_id}`}>
              <div className="flex items-start justify-between">
                <div className="flex-1">
                  <div className="flex items-center gap-2 flex-wrap">
                    <h3 className="text-white font-medium">{t.title}</h3>
                    <Badge className={`text-[10px] border ${STATUS_COLORS[t.status]}`}>{t.status?.replace("_", " ")}</Badge>
                    <Badge className={`text-[10px] ${PRIORITY_COLORS[t.priority]}`}>{t.priority}</Badge>
                    {t.unit && <Badge variant="outline" className="text-[10px] border-emerald-700 text-emerald-400">{t.unit}</Badge>}
                  </div>
                  <p className="text-slate-400 text-xs mt-1 line-clamp-1">{t.description}</p>
                  <div className="flex items-center gap-4 mt-2 text-[10px] text-slate-500">
                    <span className="flex items-center gap-1"><User className="w-3 h-3" /> {t.assigned_to_name || "Unassigned"}</span>
                    <span>By: {t.assigned_by_name}</span>
                    {t.due_date && <span className="flex items-center gap-1"><Clock className="w-3 h-3" /> {t.due_date}</span>}
                    {t.comments?.length > 0 && <span className="flex items-center gap-1"><MessageSquare className="w-3 h-3" /> {t.comments.length}</span>}
                    {t.photos?.length > 0 && <span className="flex items-center gap-1"><Image className="w-3 h-3" /> {t.photos.length}</span>}
                  </div>
                </div>
                <div className="flex gap-1.5 ml-3 shrink-0">
                  {t.status === "OPEN" && <button onClick={(e) => { e.stopPropagation(); updateStatus(t.task_id, "IN_PROGRESS"); }} className="px-2 py-1 bg-amber-600/20 text-amber-400 rounded text-[10px] hover:bg-amber-600/30">Start</button>}
                  {t.status === "IN_PROGRESS" && <button onClick={(e) => { e.stopPropagation(); updateStatus(t.task_id, "DONE"); }} className="px-2 py-1 bg-emerald-600/20 text-emerald-400 rounded text-[10px] hover:bg-emerald-600/30">Done</button>}
                </div>
              </div>
            </Card>
          ))
        }
      </div>

      {/* Task Detail Dialog */}
      <Dialog open={!!selectedTask} onOpenChange={() => setSelectedTask(null)}>
        <DialogContent className="sm:max-w-lg bg-[#1A2332] border-emerald-900/50 text-white max-h-[85vh] overflow-y-auto">
          <DialogHeader><DialogTitle className="text-white font-['Sora']">{selectedTask?.title}</DialogTitle></DialogHeader>
          {selectedTask && (
            <div className="space-y-4">
              <div className="flex gap-2 flex-wrap">
                <Badge className={`border ${STATUS_COLORS[selectedTask.status]}`}>{selectedTask.status?.replace("_", " ")}</Badge>
                <Badge className={PRIORITY_COLORS[selectedTask.priority]}>{selectedTask.priority}</Badge>
                {selectedTask.unit && <Badge variant="outline" className="border-emerald-700 text-emerald-400">{selectedTask.unit}</Badge>}
              </div>
              <p className="text-slate-300 text-sm">{selectedTask.description || "No description"}</p>
              <div className="grid grid-cols-2 gap-3 text-xs text-slate-400">
                <div>Assigned to: <span className="text-white">{selectedTask.assigned_to_name || "Unassigned"}</span></div>
                <div>Created by: <span className="text-white">{selectedTask.assigned_by_name}</span></div>
                {selectedTask.due_date && <div>Due: <span className="text-white">{selectedTask.due_date}</span></div>}
              </div>
              {/* Comments */}
              <div>
                <h4 className="text-sm text-emerald-400 font-medium mb-2">Comments ({selectedTask.comments?.length || 0})</h4>
                <div className="space-y-2 max-h-40 overflow-y-auto">
                  {(selectedTask.comments || []).map((c, i) => (
                    <div key={i} className="bg-[#0F1A1A] rounded-lg p-2 text-xs">
                      <span className="text-emerald-400 font-medium">{c.by_name}</span>
                      <span className="text-slate-500 ml-2">{new Date(c.at).toLocaleString("en-IN", { dateStyle: "short", timeStyle: "short" })}</span>
                      <p className="text-slate-300 mt-1">{c.text}</p>
                    </div>
                  ))}
                </div>
                <div className="flex gap-2 mt-2">
                  <Input value={comment} onChange={(e) => setComment(e.target.value)} placeholder="Add comment..." className="bg-[#1C2B2B] border-emerald-900/50 text-white text-xs" onKeyDown={(e) => e.key === "Enter" && addComment()} data-testid="task-comment-input" />
                  <Button size="sm" onClick={addComment} className="bg-emerald-600 text-white text-xs" data-testid="send-comment-btn">Send</Button>
                </div>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Create Task Dialog */}
      <Dialog open={showAdd} onOpenChange={setShowAdd}>
        <DialogContent className="sm:max-w-md bg-[#1A2332] border-emerald-900/50 text-white">
          <DialogHeader><DialogTitle className="text-white font-['Sora']">New Task</DialogTitle></DialogHeader>
          <form onSubmit={handleSubmit} className="space-y-3">
            <div>
              <label className="text-xs text-emerald-600 font-medium">Title</label>
              <Input value={form.title} onChange={(e) => setForm({...form, title: e.target.value})} required className="bg-[#1C2B2B] border-emerald-900/50 text-white mt-1" data-testid="task-title" />
            </div>
            <div>
              <label className="text-xs text-emerald-600 font-medium">Description</label>
              <textarea value={form.description} onChange={(e) => setForm({...form, description: e.target.value})} rows={2} className="w-full mt-1 px-3 py-2 bg-[#1C2B2B] border border-emerald-900/50 text-white text-sm rounded-md" data-testid="task-desc" />
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="text-xs text-emerald-600 font-medium">Assign To</label>
                <Select value={form.assigned_to} onValueChange={(v) => {
                  const u = users.find(x => x.user_id === v);
                  setForm({...form, assigned_to: v, assigned_to_name: u?.name || ""});
                }}>
                  <SelectTrigger className="bg-[#1C2B2B] border-emerald-900/50 text-white mt-1"><SelectValue placeholder="Select..." /></SelectTrigger>
                  <SelectContent>{users.map(u => <SelectItem key={u.user_id} value={u.user_id}>{u.name} {u.assigned_unit ? `(${u.assigned_unit})` : ""}</SelectItem>)}</SelectContent>
                </Select>
              </div>
              <div>
                <label className="text-xs text-emerald-600 font-medium">Priority</label>
                <Select value={form.priority} onValueChange={(v) => setForm({...form, priority: v})}>
                  <SelectTrigger className="bg-[#1C2B2B] border-emerald-900/50 text-white mt-1"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {["LOW", "MEDIUM", "HIGH", "URGENT"].map(p => <SelectItem key={p} value={p}>{p}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="text-xs text-emerald-600 font-medium">Due Date</label>
                <Input type="date" value={form.due_date} onChange={(e) => setForm({...form, due_date: e.target.value})} className="bg-[#1C2B2B] border-emerald-900/50 text-white mt-1" />
              </div>
              <div>
                <label className="text-xs text-emerald-600 font-medium">Unit</label>
                <Select value={form.unit} onValueChange={(v) => setForm({...form, unit: v})}>
                  <SelectTrigger className="bg-[#1C2B2B] border-emerald-900/50 text-white mt-1"><SelectValue placeholder="Optional" /></SelectTrigger>
                  <SelectContent><SelectItem value="Unit 1">Unit 1</SelectItem><SelectItem value="Unit 2">Unit 2</SelectItem></SelectContent>
                </Select>
              </div>
            </div>
            <div>
              <label className="text-xs text-emerald-600 font-medium">Attachments</label>
              <label className="flex items-center gap-2 mt-1 px-3 py-2 border border-dashed border-emerald-700 rounded-md cursor-pointer hover:border-emerald-500 bg-[#1C2B2B]">
                <Camera className="w-4 h-4 text-emerald-500" />
                <span className="text-sm text-slate-400">{photoFiles.length > 0 ? `${photoFiles.length} file(s)` : "Upload photos/documents"}</span>
                <input type="file" className="hidden" accept="image/*,.pdf,.doc,.docx" multiple onChange={(e) => setPhotoFiles(Array.from(e.target.files || []))} />
              </label>
            </div>
            <Button type="submit" disabled={creating} className="w-full bg-emerald-600 hover:bg-emerald-700 text-white" data-testid="submit-task-btn">
              {creating ? "Creating..." : "Create Task"}
            </Button>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default FactoryTasksPage;
